import {
  faCheckCircle,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { useAtomValue, useSetAtom } from 'jotai';
import { useHistory } from 'react-router';

import type { TableColumn } from '../../../common-components/composed/sortableTable/SortableTable';
import { SortableTable } from '../../../common-components/composed/sortableTable/SortableTable';
import { StatusChip } from '../../../common-components/elements/StatusChip/StatusChip';
import { useGetDateFNSLocale } from '../../../locales/helpers';
import { type Appointment } from '../../../react-query/useAppointments';
import routesConstants from '../../../routes/routes.constants';
import { getProviderName } from '../../patientList/huddlePrintoutContent/helpers';
import { PatientInfo } from '../../patientList/patientStrip/patientStripRedesigned/patientInfoSheet/PatientInfo';
import { PatientListPageLayoutContextMap } from '../../patientList/types';
import type { TTreatmentResponse } from '../freeClaims/insuranceExport/types';
import {
  cleanClaimsAttachmentValuesAtom,
  useLocalizedFormatDate,
} from '../freeClaims/state';

import { GenerateClaimAttachmentButton } from './claim/GenerateClaimAttachmentButton';
import { filterAppointmentsByClaimStatus, getDisplayName } from './helpers';
import { useClaimsSorting } from './hooks/useClaimsSorting';
import { showClaimsBanner } from './state';

interface ClaimsTableProps {
  appointments: (Appointment & {
    treatments: TTreatmentResponse[];
    allTreatmentsExcluded: boolean;
  })[];
  selectedRows?: string[];
  isSearchActive?: boolean;
  onSetSelectedRows: (rows: string[]) => void;
}

export const ClaimsTable = ({
  appointments,
  isSearchActive = false,
}: ClaimsTableProps) => {
  // atoms
  const setCleanClaimsAttachmentValues = useSetAtom(
    cleanClaimsAttachmentValuesAtom,
  );
  const showBanner = useAtomValue(showClaimsBanner);

  // hooks
  const history = useHistory();
  const locale = useGetDateFNSLocale();
  const formatDate = useLocalizedFormatDate();

  // Sorting logic
  const { sortedAppointments, sortField, sortDirection, handleSort } =
    useClaimsSorting(appointments, isSearchActive);

  // Define columns for SortableTable
  const columns: TableColumn<
    Appointment & {
      treatments: TTreatmentResponse[];
      allTreatmentsExcluded: boolean;
    }
  >[] = [
    {
      key: 'patient',
      header: <Trans>Patient</Trans>,
      width: 'vai-w-[282px]',
      isSortable: true,
      render: (appointment, index) => (
        <PatientInfo
          context={PatientListPageLayoutContextMap.Claims}
          appointment={appointment}
          index={index}
        />
      ),
    },
    {
      key: 'appointment',
      header: <Trans>Service Date</Trans>,
      width: 'vai-w-[211px]',
      isSortable: true,
      render: (appointment) => (
        <>
          {isSearchActive
            ? formatDate(appointment.appointment.start, false, 'P')
            : formatDate(appointment.appointment.start, false, 'h:mmaaa')}
        </>
      ),
    },
    {
      key: 'provider',
      header: <Trans>Provider Name</Trans>,
      width: 'vai-w-[211px]',
      isSortable: true,
      render: (appointment) => (
        <>
          {appointment.appointment.provider?.firstName}{' '}
          {appointment.appointment.provider?.lastName}
        </>
      ),
    },
    {
      key: 'serviceCodes',
      header: <Trans>Service Codes</Trans>,
      render: (appointment) => {
        const sortedTreatments = appointment.treatments || [];
        return (
          <div className="vai-flex vai-gap-1 vai-flex-wrap">
            {[...new Set(sortedTreatments.map((t) => t.txCode))].map(
              (txCode) => (
                <div
                  key={txCode}
                  className="vai-bg-slate-200 vai-text-zinc-900 vai-rounded-full vai-w-[68px] vai-px-3 vai-py-1 vai-text-center"
                >
                  {txCode}
                </div>
              ),
            )}
          </div>
        );
      },
    },
    {
      key: 'services',
      header: <Trans>Services</Trans>,
      render: (appointment) => (
        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-600">
            {[
              // Get unique service names from treatments
              ...new Set(
                appointment.treatments
                  .map(
                    // Get the service name
                    (t) => t.txGroupName || '',
                  )
                  // Remove potential empty strings resulting from missing translations
                  .filter(Boolean),
              ),
            ].join(', ')}
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      header: <Trans>Status</Trans>,
      isSortable: true,
      render: (appointment) => {
        if (appointment.allTreatmentsExcluded) {
          return (
            <span className="vai-italic">
              <Trans>No Attachment Needed</Trans>
            </span>
          );
        }

        const isClaimAttachmentReady = Boolean(
          filterAppointmentsByClaimStatus(appointment, 'ready'),
        );

        return (
          <StatusChip
            text={
              isClaimAttachmentReady ? (
                <Trans>Ready to Submit</Trans>
              ) : (
                <Trans>Needs Review</Trans>
              )
            }
            variant={isClaimAttachmentReady ? 'success' : 'warning'}
            icon={
              <FontAwesomeIcon
                icon={
                  isClaimAttachmentReady ? faCheckCircle : faExclamationTriangle
                }
              />
            }
          />
        );
      },
    },
    {
      key: 'actions',
      header: <Trans>Actions</Trans>,
      width: 'vai-w-[100px]',
      render: (appointment) => {
        if (appointment.allTreatmentsExcluded) {
          return null;
        }

        const isClaimAttachmentReady = Boolean(
          filterAppointmentsByClaimStatus(appointment, 'ready'),
        );

        return (
          <div className="vai-flex vai-justify-end vai-gap-2">
            <GenerateClaimAttachmentButton
              onClick={() => handleClickExport(appointment)}
              appointment={appointment}
              variant={isClaimAttachmentReady ? 'outline' : 'default'}
            />
            {/* --- PLACEHOLDER DOWNLOAD BUTTON FOR REDESIGN --- */}
            {/* TODO: add functional download button */}
            {/* <Button variant="ghost" className="!vai-px-3">
              <FontAwesomeIcon icon={faArrowDownToLine} />
            </Button> */}
          </div>
        );
      },
    },
  ];

  // handlers
  const handleClickExport = (appointment: Appointment) => {
    setCleanClaimsAttachmentValues({
      unifiedId: appointment.appointment.unifiedId,
      estimatedVisitId:
        appointment.appointment.estimatedVisitId?.toString() ?? '',
      appointment,
      patientInfo: {
        name: getDisplayName(appointment, 0).patientName,
        dob: formatDate(appointment.patient.dob ?? '', true),
        dateOfVisit: formatDate(appointment?.appointment.start || ''),
        providerName: getProviderName(appointment?.appointment.provider),
      },
      locale,
    });

    const unifiedId = appointment.appointment.unifiedId ?? '';
    const estimatedVisitId =
      appointment.appointment.estimatedVisitId?.toString() ?? '';
    history.push(`${routesConstants.CLAIMS}/${unifiedId}/${estimatedVisitId}`);
  };

  return (
    <SortableTable
      data={sortedAppointments}
      columns={columns}
      sortField={sortField}
      sortDirection={sortDirection}
      onSort={handleSort}
      rowKey={(appointment) => `claim-${appointment.appointment.unifiedId}`}
      className={showBanner ? 'vai-h-[61vh]' : 'vai-h-[74vh]'}
    />
  );
};
